import React, { useMemo, useState, useEffect, useRef } from "react";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useCommodityGroups } from "../../lib/commodity";
import { ChevronRight, Search, FolderTree, X, Check } from "lucide-react";

// Build the ancestor id chain for a given node id (walks parentId up).
function ancestorsOf(byId, id) {
  const chain = [];
  let cur = byId[id];
  while (cur && cur.parentId) {
    chain.push(cur.parentId);
    cur = byId[cur.parentId];
  }
  return chain;
}

// Compute the ordered list of currently visible rows from the pre-order `flat`
// list, honouring the expanded set. When a search term is present, every
// matching node (and, implicitly, its indented context) is shown regardless of
// expand state.
function useVisibleRows(flat, byId, expanded, query) {
  return useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (q) {
      const keep = new Set();
      for (const n of flat) {
        const hit =
          n.name.toLowerCase().includes(q) ||
          (n.code || "").toLowerCase().includes(q) ||
          n.path.toLowerCase().includes(q);
        if (hit) {
          keep.add(n.id);
          for (const a of ancestorsOf(byId, n.id)) keep.add(a);
        }
      }
      return flat.filter((n) => keep.has(n.id));
    }
    const visible = new Set();
    const out = [];
    for (const n of flat) {
      const shown = n.parentId === null || (expanded.has(n.parentId) && visible.has(n.parentId));
      if (shown) {
        visible.add(n.id);
        out.push(n);
      }
    }
    return out;
  }, [flat, byId, expanded, query]);
}

// A single tree row (shared by the single- and multi-select variants).
function TreeRow({ node, expanded, onToggleExpand, selected, onPick, testPrefix, multi }) {
  const isOpen = expanded.has(node.id);
  return (
    <div
      className={`group flex items-center gap-1.5 pr-2 py-1.5 rounded-md transition-colors cursor-pointer ${
        selected ? "bg-amber-50 ring-1 ring-amber-200" : "hover:bg-gray-50"
      }`}
      style={{ paddingLeft: 6 + node.level * 16 }}
      data-testid={`${testPrefix}-node-${node.id}`}
      onClick={() => onPick(node)}
    >
      {node.hasChildren ? (
        <button
          type="button"
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-200 hover:text-gray-700"
          onClick={(e) => { e.stopPropagation(); onToggleExpand(node.id); }}
          data-testid={`${testPrefix}-toggle-${node.id}`}
        >
          <ChevronRight className={`h-3.5 w-3.5 transition-transform duration-150 ${isOpen ? "rotate-90" : ""}`} strokeWidth={2.5} />
        </button>
      ) : (
        <span className="h-5 w-5 shrink-0" />
      )}
      {multi ? (
        <Checkbox
          checked={selected}
          onCheckedChange={() => onPick(node)}
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testPrefix}-checkbox-${node.id}`}
        />
      ) : node.hasChildren ? (
        <FolderTree className="h-3.5 w-3.5 shrink-0 text-[#1B2A4A]/60" />
      ) : (
        <span className="ml-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#F5A623]" />
      )}
      {node.code ? <span className="font-mono text-[11px] text-gray-400 shrink-0">{node.code}</span> : null}
      <span className={`text-sm truncate ${node.hasChildren ? "font-medium text-gray-800" : "text-gray-700"}`}>{node.name}</span>
      {!multi && selected ? <Check className="ml-auto h-3.5 w-3.5 shrink-0 text-amber-600" strokeWidth={2.5} /> : null}
    </div>
  );
}

// Shared expand-state hook that keeps roots + ancestors of the current
// selection open by default. Initialises exactly once (the tree's `flat`/`byId`
// get a fresh identity every render, so we guard with a ref to avoid a loop).
function useExpanded(flat, byId, selectedIds) {
  const [expanded, setExpanded] = useState(new Set());
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current || flat.length === 0) return;
    initRef.current = true;
    const init = new Set(flat.filter((n) => n.level === 0 && n.hasChildren).map((n) => n.id));
    for (const id of selectedIds) for (const a of ancestorsOf(byId, id)) init.add(a);
    setExpanded(init);
  }, [flat, byId, selectedIds]);
  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  return [expanded, toggle];
}

// Single-select of one commodity group, rendered as a collapsible tree inside a
// popover with a live search box and a clear button.
export function CommodityGroupSelect({ value = "", onChange, testId = "commodity-group-select" }) {
  const { flat, byId, loading } = useCommodityGroups();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selectedIds = useMemo(() => (value ? [value] : []), [value]);
  const [expanded, toggleExpand] = useExpanded(flat, byId, selectedIds);
  const rows = useVisibleRows(flat, byId, expanded, query);
  const selectedPath = value && byId[value] ? byId[value].path : "";

  const pick = (node) => { onChange(node.id); setOpen(false); setQuery(""); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          disabled={loading}
          className="mt-1 flex w-full items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 h-9 text-sm text-left hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <FolderTree className="h-4 w-4 shrink-0 text-gray-400" />
          <span className={`flex-1 truncate ${selectedPath ? "text-gray-800" : "text-gray-400"}`}>
            {loading ? "Lädt …" : selectedPath || "Warengruppe wählen …"}
          </span>
          {value ? (
            <span
              className="text-gray-400 hover:text-red-500"
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              data-testid={`${testId}-clear`}
            >
              <X className="h-3.5 w-3.5" />
            </span>
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-gray-400 rotate-90" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] min-w-[280px] p-0" data-testid={`${testId}-panel`}>
        <div className="relative border-b border-gray-100 p-2">
          <Search className="absolute left-4 top-4 h-3.5 w-3.5 text-gray-400" />
          <Input
            autoFocus
            className="h-8 pl-7 text-sm"
            placeholder="Suchen …"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid={`${testId}-search`}
          />
        </div>
        <div className="max-h-72 overflow-y-auto p-1.5">
          {loading ? (
            <div className="p-3 text-sm text-gray-400">Lädt …</div>
          ) : rows.length === 0 ? (
            <div className="p-3 text-sm text-gray-400">Keine Warengruppen gefunden.</div>
          ) : (
            rows.map((n) => (
              <TreeRow
                key={n.id}
                node={n}
                expanded={expanded}
                onToggleExpand={toggleExpand}
                selected={value === n.id}
                onPick={pick}
                testPrefix="cg-tree"
                multi={false}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Multi-select of commodity groups from the shared tree (collapsible tree with
// selected chips above). Used for supplier commodity coverage.
export function CommodityGroupPicker({ value = [], onChange }) {
  const { flat, byId, loading } = useCommodityGroups();
  const [query, setQuery] = useState("");
  const [expanded, toggleExpand] = useExpanded(flat, byId, value);
  const rows = useVisibleRows(flat, byId, expanded, query);
  const toggle = (id) =>
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);

  return (
    <div data-testid="commodity-group-picker">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map((id) => (
            <Badge key={id} variant="outline" className="gap-1 text-xs" data-testid={`cg-chip-${id}`}>
              {(byId[id] && byId[id].path) || id}
              <button type="button" className="text-gray-400 hover:text-red-500 ml-0.5"
                onClick={() => toggle(id)} data-testid={`cg-chip-remove-${id}`}>×</button>
            </Badge>
          ))}
        </div>
      )}
      <div className="relative mb-1.5">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
        <Input className="h-8 pl-7 text-sm" placeholder="Suchen …" value={query}
          onChange={(e) => setQuery(e.target.value)} data-testid="cg-pick-search" />
      </div>
      <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200 bg-white p-1.5">
        {loading ? (
          <div className="p-3 text-sm text-gray-400">Lädt …</div>
        ) : rows.length === 0 ? (
          <div className="p-3 text-sm text-gray-400">Keine Warengruppen gepflegt. Bitte in den Einstellungen anlegen.</div>
        ) : (
          rows.map((n) => (
            <TreeRow
              key={n.id}
              node={n}
              expanded={expanded}
              onToggleExpand={toggleExpand}
              selected={value.includes(n.id)}
              onPick={(node) => toggle(node.id)}
              testPrefix="cg-pick"
              multi={true}
            />
          ))
        )}
      </div>
    </div>
  );
}
